<template>
    <modal-centered-base
    v-if="!okChallengeType"
    fullScreenDialog
    modalWidth="448px"
    footerMarginTop="16px"
    preventClickOutsideClose
    titleIcon="document-confirm"
    helperTextTitle
    :rightButtonIcon="secureAssociationToken ? 'arrow-right-full': 'check-line'"
    :title="modalTitle"
    :rightButtonText="buttonText"
    :triggerClose="triggerClose"
    @hideMe="emit('hideMe')"
    @clicked="emit('hideMe')"
    @backOrCloseClicked="emit('challengeCancelHandler')"
    @leftButtonClicked="emit('challengeCancelHandler')"
    @rightButtonClicked="inputValidator">
        <div v-if="!secureAssociationToken" class="token-content-wrapper" :class="{'mobile': getMobileAppState}">
            <div class="instruction">{{instructionMessage}}</div>
            <!-- Inputs -->
            <div class="inputs-wrapper" :class="{'adjust-margin': applyErrorState && code.join('').length < 6 }">
                <input-otp
                v-for="position in 6"
                ref="otpInputRef"
				:applyAutoCompleteOneTime="position === 0"
                :errorState="applyErrorState && !code[position-1]"
                :key="position"
                :value="code[position-1]"
                @backspaceHandler="backspaceHandler(position, $event)"
                @inputPositionChanged="inputPositionChanged(position, $event)"
                @pasteData="pasteData"/>
            </div>
            <!-- Error Message -->
            <div v-if="applyErrorState && code.join('').length < 6" class="error-message">
                {{ t('mandatoryFieldError') }}
            </div>
            <div v-if="getMobileAppState" class="resent-wrapper">
                <div class="resend-message"> {{ getAllCustomerInfo ? resendMessage : t('modals.token.telephoneNote') }} </div>
                <button-link
                internalClick
                :label="t('modals.token.resendBtn')"
                :disabled="!resendActive"
                @clicked="requestNewTokenHandler"/>
            </div>
		</div>
        <div v-if="secureAssociationToken" class="token-content-wrapper">
            <div :key="`biometricType${getMobileBiometric}`" class="instruction">{{t('modals.token.biometricValidateMessage', { biometricType: biometricType })}}</div>
        </div>
        <!-- Resend info and button -->
        <template v-if="!getMobileAppState" #modal-after-footer-content>
            <div class="resent-wrapper">
                <div class="resend-message"> {{ getAllCustomerInfo ? resendMessage : t('modals.token.telephoneNote') }} </div>
                <button-link
                internalClick
                :label="t('modals.token.resendBtn')"
                :disabled="!resendActive"
                @clicked="requestNewTokenHandler"/>
            </div>
        </template>
    </modal-centered-base>
    <!-- Loader Generic -->
    <loaders-generic v-if="showLoader" isOverlay/>
    <!-- Feedback Toasts -->
    <toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import ModalCenteredBase from '@/components/generic/modals/centered/Base'
import InputOtp from '@/components/generic/inputs/OTP'
import ButtonLink from '@/components/generic/buttons/Link'
import LoadersGeneric from '@/components/generic/loaders/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* Vue and Aux */
import { ref, computed, onUnmounted, onBeforeUnmount, onMounted } from 'vue'
import { errorObjectConstructor, sucessObjectConstrutor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import store from '@/store/store'
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['hideMe', 'challengeCancelHandler', 'challengeSuccessHandler', 'challengeErrorHandler'])

/* Props
@titleAppend {String} - title to append to token modal base
@titleFull {String} - title to token modal base
@params {Object} - some times we will need additional queryParams to send with token submition, when so, send it in this prop
@modalCustomUseParams {Object} - when we want to have this modal but with apis diferent than the generic ones we send this object with:
    - messageToDisplay {String}
    - sendTokenAction {String} - 'moduleName/actionName'
    - resendTokenAction {String} - 'moduleName/actionName'
    - payload {Object} info that we want to send along with the response
    - resendPayload {Object} with info needed to resent token
    - forceResendChannelString {String} - ['email', 'sms'] - prop to ignore session token channel and use custom one
*/
const props = defineProps({
    titleAppend: String,
    titleFull: String,
    params: Object,
    modalCustomUseParams: Object
})

const { submitChallenge, requestNewChallenge, updatePreventMobileFeatureBack, updatePreventMobileSideModalBack } = mapActions()
const { getTokenModalInfo, getMobileAppState } = mapGetters()
const { getAllCustomerInfo, getMobileBiometric } = mapGetters(moduleMapper.SESSION)

const secureAssociationToken = computed(() => { return getTokenModalInfo.value?.response.challenge.type === 'SECURE_ASSOCIATION' })
const safTemplate = computed(() => { return getTokenModalInfo.value?.response.challenge.safTemplate })
const biometricType = computed(() => { return getMobileBiometric.value === 'face' ? t('modals.token.useIosFace') : t('modals.token.biometricButton') })

/* Messages based on receiver type */
const emailReceiver = computed(() => { return getAllCustomerInfo.value?.customer.receivesChallengeViaAlternativeChannel })

const modalTitle = computed(() => {
    if (secureAssociationToken.value) return t('modals.token.biometricValidateTitle')

    return props.titleFull ? props.titleFull : `${t('modals.token.validateTitle')} ${props.titleAppend}`
})

const buttonText = computed(() => {
    return secureAssociationToken.value ? t('modals.token.continue') : t('modals.token.validateBtn')
})

/*
* We used to use this message for modal token: getTokenModalInfo.value.response.challenge.message ,
* but new laytous have a more generic, will leave this here just in case it was an example only */
const instructionMessage = computed(() => {
    return props.modalCustomUseParams && props.modalCustomUseParams.messageToDisplay ? props.modalCustomUseParams.messageToDisplay : getTokenModalInfo.value.response.challenge.message
})

const resendMessage = computed(() => {
    if (props.modalCustomUseParams && props.modalCustomUseParams.forceResendChannelString) {
        return props.modalCustomUseParams.forceResendChannelString === 'email' ? t('modals.token.emailNote') : t('modals.token.telephoneNote')
    }
    return emailReceiver.value ? t('modals.token.emailNote') : t('modals.token.telephoneNote')
})

/*************************************************
*                                                *
*                     Aux Data                   *
*                                                *
*************************************************/
const okChallengeType = ref(false)
const showLoader = ref(false)
/* Inputs Refs, this will be an array of refs */
const otpInputRef = ref('')
// code as ordered array of single numbers representing each input
const code = ref([null, null, null, null, null, null])
// boolean defining if resend is active or not
const resendActive = ref(false)
const countingToResend = ref(45)
// timeout variable needed to work with resend activation after 45 seconds
const timeoutHolder = ref(null)
const watchingTimeouted = ref(false)
// inputs Error State
const applyErrorState = ref(false)

const backspaceHandler = (position, $event) => {
    /* If our position has no content we will back */
    if ((!code.value[position - 1] || !code.value[position - 1].length) && (position - 2 >= 0)) {
        otpInputRef.value[position - 2].$el.children[0].focus()
        code.value[position - 2] = ''
        $event.stopPropagation()
        $event.preventDefault()
    }
}

const regexValidator = string => {
	const regex = /^[0-9]{0,1}$/
	return regex.test(string)
}

const inputPositionChanged = (position, value) => {
    if (value?.length === 6) {
        code.value = value.replace(/(?:[^0-9])/g, '').substring(0, 6).split('')
        otpInputRef.value[position - 1].$el.children[0].blur()
        return
    }

    if (!regexValidator(value)) {
        code.value[position - 1] = ''
        return
    }

    code.value[position - 1] = value
    /* it focus the next input and if it's the last only blurs, leaving no inputs focused */
    if (value) {
        if (position < 6) {
            otpInputRef.value[position].$el.children[0].focus()
        } else {
            otpInputRef.value[position - 1].$el.children[0].blur()
        }
    } else {
        if (position > 1) {
            otpInputRef.value[0].$el.children[0].focus()
        }
    }
}

const startWatching = () => {
    watchingTimeouted.value = false
    if (window.cordova?.platformId === 'android' && !watchingTimeouted.value) {
        window.cordova.plugins.smsRetriever.startWatching(successCallbackSMSRetriever, errorCallbackSMSRetriever, true)
    }
}

const successCallbackSMSRetriever = data => {
    const message = data.Message
    const helper = safTemplate.value.split('%')
    const token = message.replace(helper[0], '').replace(helper[2], '')

    code.value = token.replace(/(?:[^0-9])/g, '').substring(0, 6).split('')
}

const errorCallbackSMSRetriever = error => {
    console.log(error)
}

/* handles when user pastes the code */
const pasteData = async $event => {
    const text = $event.clipboardData.getData('text')
    code.value = text.replace(/(?:[^0-9])/g, '').substring(0, 6).split('')
}

/*************************************************
*                                                *
*                Resend Token Logic                *
*                                                *
*************************************************/
const startResendTimer = () => {
    if (!timeoutHolder.value) {
        resendActive.value = false
        timeoutHolder.value = setTimeout(() => {
            timeoutHolder.value = undefined
            resendActive.value = true
        }, countingToResend.value * 1000) // 45 seconds until resend button activation
    }
}

const requestNewTokenHandler = async () => {
    showLoader.value = true
    let res

    try {
        /* Custom actions for resend token */
        if (props.modalCustomUseParams) res = await store.dispatch(`${props.modalCustomUseParams.resendTokenAction}`, props.modalCustomUseParams.resendPayload)
        /* Generic token resend */
        else {
            const payload = { challengeId: getTokenModalInfo.value.response.challenge.id }
            res = await requestNewChallenge(payload)
        }
        startWatching()
        successHandler(res.data)
    } catch (error) {
        errorHandler(error.response.data, requestNewTokenHandler)
    }
}
/*************************************************
*                                                *
*                Send Token Logic                *
*                                                *
*************************************************/
const inputValidator = () => {
    if (secureAssociationToken.value) {
        showAnswerChallengeBiometricDialog()
        return
    }

    code.value.join('').length < 6 ? applyErrorState.value = true : sendTokenClickHandler()
}

const sendTokenClickHandler = () => {
    let payload

    if (props.modalCustomUseParams && props.modalCustomUseParams.sendTokenAction) {
        payload = {
            ...props.modalCustomUseParams.payload,
            response: code.value.join('')
        }
    } else if (secureAssociationToken.value) {

    } else {
        payload = {
            challengeId: getTokenModalInfo.value.response.challenge.id,
            challengeResponse: code.value.join(''),
            params: props.params
        }
    }
    sendTokenHandler(payload)
}

const sendTokenHandler = async payload => {
    if (requestAllreadyCalled.value) return
    showLoader.value = true
    requestAllreadyCalled.value = true
    let res

    try {
        /* Custom actions for sending token */
        if (props.modalCustomUseParams && props.modalCustomUseParams.sendTokenAction) res = await store.dispatch(`${props.modalCustomUseParams.sendTokenAction}`, payload)
        /* Generic token submission */
        else res = await submitChallenge(payload)
        emit('challengeSuccessHandler', res.data)
        emit('hideMe')
    } catch (error) {
        console.log(error)
        emit('challengeErrorHandler', error.response.data)
        emit('hideMe')
    }
}

/*************************************************
*                  Initial Logic                 *
*************************************************/
const initialLogic = () => {
    if (!props.modalCustomUseParams && getTokenModalInfo.value.response.challenge.type === 'OK') {
        okChallengeType.value = true
        sendTokenHandler({ challengeId: getTokenModalInfo.value.response.challenge.id })
    } else startResendTimer()
}

/*************************************************
*                                                *
*                   Biometric                    *
*                                                *
*************************************************/
const requestAllreadyCalled = ref(false)

const showAnswerChallengeBiometricDialog = () => {
    requestAllreadyCalled.value = false
	window.Fingerprint.loadBiometricSecret({
        secretKey: 'associationKey',
		title: t('biometricLogin.title'),
		description: t('biometricLogin.authenticationDescription'),
		disableBackup: true
	}, decryptChallenge, dismissChallengeBiometricDialog)
	showLoader.value = false
}

const decryptChallenge = privateKey => {
	const binaryData = Buffer.from(getTokenModalInfo.value.response.challenge.question, 'base64')
	const temp = JSON.parse(privateKey)

	crypto.subtle.importKey(
		'jwk',
		temp,
		{
			name: 'RSA-OAEP',
			modulusLength: 2048,
			publicExponent: new Uint8Array([1, 0, 1]), // 24 bit representation of 65537
			hash: { name: 'SHA-1' }
		},
		true,
		['decrypt']
	).then(
		(key) => {
			crypto.subtle.decrypt(
				{ name: 'RSA-OAEP' },
				key,
				binaryData
			).then(
				(data) => {
					var enc = new TextDecoder('utf-8')
					var clearText = enc.decode(data)

					const payload = {
                        challengeId: getTokenModalInfo.value.response.challenge.id,
						challengeResponse: `${clearText.split('|')[0]},${clearText.split('|')[1]}`
					}

					sendTokenHandler(payload)
				}
			)
		}
	)
}

const dismissChallengeBiometricDialog = error => {
	if (error.code !== -108) {
		feedbackToastData.value.text = error.message
		feedbackToastData.value.buttons[1].callbackAction = showAnswerChallengeBiometricDialog
		showFeedbackToast.value = true
	}
}

/*************************************************
*             Success/Error Handlers             *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const successHandler = data => {
    showLoader.value = false

    feedbackToastData.value = sucessObjectConstrutor(data.message)
    showFeedbackToast.value = true
}

const errorHandler = (data, retryFn) => {
    showLoader.value = false

	feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

initialLogic()

onMounted(() => {
    startWatching()
})

onUnmounted(() => {
    clearTimeout(timeoutHolder.value)
})
/***********************************************************
*                                                          *
*                 MOBILE NATIVE BACK LOGIC                 *
*                                                          *
***********************************************************/
/* Preving mobile back to trigger features close */
updatePreventMobileFeatureBack(true)
updatePreventMobileSideModalBack(true)

onBeforeUnmount(() => {
	updatePreventMobileFeatureBack(false)
    updatePreventMobileSideModalBack(false)
})

const triggerClose = ref(false)
const onBackKeyDown = () => {
    triggerClose.value = true
}

document.addEventListener('backbutton', onBackKeyDown, false)

/*************************************************
*                                                *
*                Resend Token Logic              *
*                                                *
*************************************************/

onBeforeUnmount(() => {
    clearTimeout(timeoutHolder.value)
})

initialLogic()
</script>

<style lang="scss" scoped>
.token-content-wrapper {
    width: 100%;

    .instruction {
        line-height: 18px;
        margin-bottom: 16px;
    }

    .inputs-wrapper {
        width: 100%;
		height: auto;

		display: flex;
		flex-direction: row;
		justify-content: space-between;

		box-shadow: none;

        &.adjust-margin {
            margin-bottom: 5px;
        }
	}

    .error-message {
        @include H7-Regular;
        color: var(--negative);
    }

    &.mobile {
        .inputs-wrapper {
            gap: 6px;

            .input-content {
                width: 100%;
                flex-shrink: 1;
            }
        }
    }
}

.resent-wrapper {
    width: 100%;
    padding-top: 16px;
    margin-top: 16px;

    border-top: 1px solid var(--secondary-light-2);

    .resend-message {
        margin-bottom: 16px;

        @include H7-Regular;
        color: var(--secondary-dark-1);
    }
}
</style>
